import React from 'react';
import { CellContext } from '@tanstack/react-table';

import { AdaptiveTableCell, Icons } from '@components/common';
import Actions from '@components/common/Actions';
import AdditionalActionsOranTable
	from '@components/routes/CmModule/ConfigurationDataModelManagement/AdditionalActionsOranTable';

import { dateFormattingWithSlash } from '@utils/date';

import { IRebuildSchema, ISchemaConfigEntity, ISchemaConfigItem } from '@interfaces/cmModule/schemaConfig';

import { columnSizes } from '@constants/table';

import styles from '@components/common/Table/Table.module.scss';

export const getColumns = ({
	setSelectedSchema,
	openConfigTree,
	openDeleteConfirm,
	handleExportConfig,
	isOranVendor,
	isEditAccess,
	handleImportYangFiles,
	handleClearFolder,
	handleRebuild,
}: {
	setSelectedSchema: (config: ISchemaConfigEntity) => void;
	openConfigTree: () => void;
	openDeleteConfirm: () => void;
	handleExportConfig: (id?: string) => void;
	isOranVendor: boolean;
	isEditAccess: boolean;
	handleImportYangFiles: (name?: string) => void;
	handleClearFolder: (id?: string) => void;
	handleRebuild: (data: IRebuildSchema) => void;
}) => {
	return [
		{
			id: 'expander',
			size: columnSizes.size35,
			header: '',
			cell: ({ row }: CellContext<ISchemaConfigItem, any>) =>
				row.getCanExpand() ? (
					<span
						className={styles.expandAction}
						onClick={row.getToggleExpandedHandler()}
					>
					{row.getIsExpanded() ? Icons.chevronUp() : Icons.chevronDown()}
					</span>
				) : null,
		},
		{
			...(isOranVendor
				? {
					header: () => (
						<>
							yang model
							<br />
							collection
						</>
					),
				}
				: {
					header: 'name',
				}),
			accessorKey: isOranVendor ? 'presetName' : 'name',
			cell: ({ row: { original } }: CellContext<ISchemaConfigEntity, any>) => {
				const value = original?.name;

				return <AdaptiveTableCell text={value} />;
			},
		},
		{
			header: 'module',
			accessorKey: 'moduleName',
			isHidden: !isOranVendor,
			cell: ({ getValue }: CellContext<ISchemaConfigItem, any>) => {
				const value = getValue();

				return <AdaptiveTableCell text={value} />;
			},
		},
		{
			header: 'namespace',
			accessorKey: 'namespace',
			cell: ({ getValue }: CellContext<ISchemaConfigItem, any>) => {
				const value = getValue();

				return <AdaptiveTableCell text={value} />;
			},
		},
		{
			header: 'prefix',
			accessorKey: 'prefix',
			cell: ({ getValue }: CellContext<ISchemaConfigItem, any>) => {
				const value = getValue();

				return <AdaptiveTableCell text={value} />;
			},
		},
		{
			header: 'build version',
			accessorKey: 'version',
			isHidden: isOranVendor,
			cell: ({ getValue }: CellContext<ISchemaConfigItem, any>) => {
				const value = getValue();

				return <AdaptiveTableCell text={value} />;
			},
		},
		{
			header: 'upload date & time',
			accessorKey: 'createdAt',
			cell: ({ getValue }: CellContext<ISchemaConfigItem, any>) => {
				const value = getValue();
				const date = dateFormattingWithSlash(value);

				return value && <AdaptiveTableCell text={date} />;
			},
		},
		{
			header: 'Actions',
			accessorKey: 'actions',
			enableSorting: false,
			cell: ({ row: { original } }: CellContext<ISchemaConfigEntity, any>) => {
				const selectHandler = () => setSelectedSchema(original);

				const viewHandler = () => {
					selectHandler();
					openConfigTree();
				};

				const deleteHandler = () => {
					selectHandler();
					openDeleteConfirm();
				};

				const downloadHandler = () => {
					handleExportConfig(original?.id);
				};

				const importHandler = () => {
					handleImportYangFiles(original?.name);
				};

				const clearHandler = () => {
					handleClearFolder(original?.folderId);
				};

				const onRebuild = () => {
					handleRebuild({
						folderId: original?.folderId || '',
						presetName: original?.name || '',
					});
				};

				return (original.id ? (
					<Actions
						deleteTooltip='Delete'
						downloadHandler={downloadHandler}
						downloadTooltip='Export'
						handleDelete={deleteHandler}
						isCustomAction
						isDelete={isEditAccess}
						isDownload
						isView
						viewHandler={viewHandler}
						viewTooltip='View'
					/>
				) : isOranVendor && isEditAccess ? (
					<Actions
						customAction={
							<AdditionalActionsOranTable
								clearHandler={clearHandler}
								importHandler={importHandler}
								isClear={!!original?.subRows?.length}
								isRebuild={!!original?.isRebuildAvailable}
								onRebuild={onRebuild}
							/>
						}
						gridNumberOfColumns={1}
						isCustomAction
					/>
				) : null);
			},
		},
	];
};
